import React, { useEffect, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Grid } from '@material-ui/core';
import AboutMeContent from './RecommendationComponents/AboutMeContent';
import EditIcon from '@material-ui/icons/Edit';
import AboutMeInstructions from './RecommendationComponents/AboutMeInstructions';
import PropTypes from 'prop-types';
import { callApi } from '../../util/apiWrapper';
import { appInsights, logException } from '../../services/appInsights';
import PATH from '../../constants/paths';
import { isNonEmpty } from '../../util/stringUtil';
import SectionHeader from './RecommendationComponents/SectionHeader';
import { METHOD } from '../../constants/method';
import useRecommendationStyles from './RecommendationComponents/useRecommendationStyles';

const AboutMe = ({ senior, store, auth, handleAfterAboutMeUpdate, version, isEditDisabled, isEditAboutMeDisabled, formExtra, setFormExtra }) => {
  const classes = useRecommendationStyles();
  const [allowEdit, setAllowEdit] = useState(false);
  const [formData, setFormData] = useState({
    post_graduation_plan_selection: '',
    post_graduation_plan: '',
    accomplishments: '',
    extracurricular_activities: '',
  });
  const [initialFormData, setInitialFormData] = useState(formData);

  useEffect(() => {
    if (!senior) return;
    const { post_graduation_plan_selection, post_graduation_plan, accomplishments, extracurricular_activities } = senior || {
      post_graduation_plan_selection: '',
      post_graduation_plan: '',
      accomplishments: '',
      extracurricular_activities: '',
    };
    setFormData({
      post_graduation_plan_selection,
      post_graduation_plan,
      accomplishments,
      extracurricular_activities,
    });
    setInitialFormData({
      post_graduation_plan_selection,
      post_graduation_plan,
      accomplishments,
      extracurricular_activities,
    });
  }, [senior]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const canSubmit = (() => {
    const isNotEmpty =
      isNonEmpty(formData?.post_graduation_plan_selection) &&
      isNonEmpty(formData?.post_graduation_plan) &&
      isNonEmpty(formData?.accomplishments) &&
      isNonEmpty(formData?.extracurricular_activities);

    if (!isNotEmpty) return false;
    return Object.keys(formData).some((key) => formData[key] !== initialFormData[key]);
  })();

  const handleClickEdit = () => {
    setAllowEdit(true);
  };

  const handleClose = (event, reason) => {
    event.preventDefault();
    if (reason === 'backdropClick') {
      setAllowEdit(true);
    } else {
      setFormData(initialFormData);
      setAllowEdit(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    async function persistAboutMeInfo() {
      try {
        store.setLoading(true);
        store.setSearching(true);

        const request = {
          id: store.senior.id,
          ...formData,
        };
        const response = await callApi(auth, PATH.ABOUT_ME, METHOD.PUT, request);
        if (response.result) {
          const { post_graduation_plan_selection, post_graduation_plan, accomplishments, extracurricular_activities } = response
            ? response.result
            : {
                post_graduation_plan_selection: '',
                post_graduation_plan: '',
                accomplishments: '',
                extracurricular_activities: '',
              };
          setInitialFormData({
            post_graduation_plan_selection,
            post_graduation_plan,
            accomplishments,
            extracurricular_activities,
          });
          handleAfterAboutMeUpdate(response.result);
        }
        appInsights.trackTrace({ message: `Saved AboutMe for user id: ${store.senior.id}` });

        store.setSearching(false);
        store.setLoading(false);
      } catch (error) {
        logException(error);
      } finally {
        store.setSearching(false);
        store.setLoading(false);
      }
    }

    persistAboutMeInfo().then(() => {
      setAllowEdit(false);
    });
  };

  return (
    <div style={{ width: '90%', margin: '0 10px 0 20px' }}>
      <Grid container align="left">
        <Box>
          <AboutMeInstructions />
          {version === 2 && (
            <div style={{ margin: '0 0 20px 0', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
              <Grid item xs={12} sm={12} md={12} lg={12} className={classes.textRowLabelSpacer} style={{ width: '96%', margin: '-10px 0 10px 20px' }}>
                This request is for (check all that apply):
              </Grid>
              <Grid item xs={12} sm={12} md={10} lg={9} className={classes.textRowSpacer} style={{ marginLeft: '40px', marginBottom: '10px' }}>
                <FormControlLabel
                  style={{ lineHeight: '1.7rem', alignItems: 'flex-start' }}
                  control={
                    <Checkbox
                      color="default"
                      disabled={isEditDisabled}
                      checked={formExtra.scholarships ? formExtra.scholarships : false}
                      onChange={() => setFormExtra({ ...formExtra, scholarships: !formExtra.scholarships })}
                      style={{ marginRight: '15px', marginTop: '-5px' }}
                    />
                  }
                  label={
                    <span>
                      Scholarships: Need TWO recommendations and cannot have any direct care staff from FHP or SF (Family Teachers, Senior Assistant
                      Family Teachers, Assistant Family Teachers, Successful Futures Staff) being the recommenders.
                    </span>
                  }
                />
              </Grid>
              <Grid item xs={12} sm={12} md={10} lg={9} className={classes.textRowSpacer} style={{ marginLeft: '40px', marginBottom: '10px' }}>
                <FormControlLabel
                  style={{ alignItems: 'flex-start' }}
                  control={
                    <Checkbox
                      color="default"
                      disabled={isEditDisabled}
                      checked={formExtra.tradeLife ? formExtra.tradeLife : false}
                      onChange={() => setFormExtra({ ...formExtra, tradeLife: !formExtra.tradeLife })}
                      style={{ marginRight: '15px', marginTop: '-5px' }}
                    />
                  }
                  label={
                    <span>
                      Community Supports: Need TWO recommendations and cannot have any direct care staff from FHP or SF (Family Teachers, Senior
                      Assistant Family Teachers, Assistant Family Teachers, Successful Futures Staff) being the recommenders.
                    </span>
                  }
                />
              </Grid>
              <Grid item xs={12} sm={12} md={10} lg={9} className={classes.textRowSpacer} style={{ marginLeft: '40px', marginBottom: '20px' }}>
                <FormControlLabel
                  style={{ alignItems: 'flex-start' }}
                  control={
                    <Checkbox
                      color="default"
                      disabled={isEditDisabled}
                      checked={formExtra.housing ? formExtra.housing : false}
                      onChange={() => setFormExtra({ ...formExtra, housing: !formExtra.housing })}
                      style={{ marginRight: '15px', marginTop: '-5px' }}
                    />
                  }
                  label={
                    <span>
                      Housing: Need THREE recommendations and cannot have any direct care staff from FHP or SF (Family Teachers, Senior Assistant
                      Family Teachers, Assistant Family Teachers, Successful Futures Staff) being the recommenders. However, two complete
                      recommendations must come from FHP program (Consultants, Directors, Senior Directors).
                    </span>
                  }
                />
              </Grid>
            </div>
          )}
          <SectionHeader title="About Me" icon={EditIcon} onTitleClick={handleClickEdit} disabled={isEditAboutMeDisabled} />
          {!allowEdit && (
            <AboutMeContent
              formData={formData}
              handleChange={handleChange}
              handleSubmit={handleSubmit}
              enableSubmit={canSubmit}
              isDialog={false}
              version={version}
            />
          )}
          <AboutMeContent
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            enableSubmit={canSubmit}
            open={allowEdit}
            handleClose={handleClose}
            isDialog={true}
            version={version}
          />
        </Box>
      </Grid>
    </div>
  );
};

AboutMe.propTypes = {
  senior: PropTypes.object.isRequired,
  store: PropTypes.object,
  auth: PropTypes.object,
  classes: PropTypes.object,
  handleAfterAboutMeUpdate: PropTypes.func.isRequired,
  version: PropTypes.number,
  formExtra: PropTypes.object.isRequired,
  setFormExtra: PropTypes.func.isRequired,
  isEditDisabled: PropTypes.bool,
  isEditAboutMeDisabled: PropTypes.bool,
};

export default AboutMe;
